import { difference } from 'lodash-es';
import { BrandSafetyType, AdgroupFormData, CampaignFormData, BrandSafetyCenterInfo } from '@tt4b/ttam-enums';
import { useStdAdgroupIs, StateEnum, getStore } from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { computed, Ref, ref } from '@vue/composition-api';
import VueI18n from 'vue-i18n';

type TipType = {
  all: VueI18n.TranslateResult;
  placement: VueI18n.TranslateResult;
  country: VueI18n.TranslateResult;
};

export type InventoryFilterOptions = {
  value: BrandSafetyType;
  label: string;
  desc: string;
  exclusionExamples: string[];
  reach: {
    summary: string;
    percentage: number;
  };
};

// 记住自动被升级为expanded的提示语
export const autoUpgradeToFullTip = ref('');

export function useInventoryFilterDisabled(options: {
  adgroupFormData: Ref<AdgroupFormData>;
  campaignFormData: Ref<CampaignFormData>;
}) {
  const { isOnlyTiktok, isSupportExpandedTire } = useStdAdgroupIs(options);
  const { adgroupFormData, campaignFormData } = options;
  const { data } = getStore(
    StateEnum.brandSafetyAvailableCountryList,
    {
      objectiveType: campaignFormData.value.objectiveType,
      classify: adgroupFormData.value.classify,
      inventoryFlow: adgroupFormData.value.inventoryFlow,
      brandSafety: BrandSafetyType.STANDARD_INVENTORY,
      switchToNewBrandSafetyFilter: true,
    },
    true,
  );

  const setAutoUpgradeToFullTip = tip => {
    autoUpgradeToFullTip.value = tip;
  };

  const isInventoryFilterLimitedForPlacement = computed(() => !isOnlyTiktok.value);

  const isInventoryFilterLimitedForLocations = computed(
    () =>
      adgroupFormData.value.country.length === 0 ||
      difference(adgroupFormData.value.country, data.value?.map(Number)).length > 0,
  );

  const isInventoryFilterLimited = computed(
    () => isInventoryFilterLimitedForPlacement.value || isInventoryFilterLimitedForLocations.value,
  );

  const getInventoryFilterTip = (type: keyof TipType) => {
    const limitedTip: TipType = {
      all: i18n.t('content_exclusions_grayout_tooltips_limitation_placements_locations'),
      placement: i18n.t('content_exclusions_grayout_tooltips_limitation_placements'),
      country: i18n.t('content_exclusions_grayout_tooltips_limitation_locations'),
    };

    const unavailableTip: TipType = {
      all: i18n.t('adgroup_placement_brandSafety_disable_for_placement_and_country'),
      placement: i18n.t('adgroup_placement_brandSafety_disable_for_placement'),
      country: i18n.t('adgroup_placement_brandSafety_disable_for_country'),
    };

    return isSupportExpandedTire.value ? unavailableTip[type] : limitedTip[type];
  };

  const inventoryFilterLimitedTip = computed(() => {
    if (isInventoryFilterLimitedForPlacement.value && isInventoryFilterLimitedForLocations.value) {
      return getInventoryFilterTip('all');
    } else if (isInventoryFilterLimitedForPlacement.value) {
      return getInventoryFilterTip('placement');
    } else if (isInventoryFilterLimitedForLocations.value) {
      return getInventoryFilterTip('country');
    } else {
      return '';
    }
  });

  const isInventoryFilterUnavailable = computed(
    () => isSupportExpandedTire.value && Boolean(inventoryFilterLimitedTip.value),
  );

  return {
    isSupportExpandedTire,
    inventoryFilterLimitedTip,
    isInventoryFilterLimited,
    isInventoryFilterUnavailable,
    isInventoryFilterLimitedForLocations,
    isInventoryFilterLimitedForPlacement,
    setAutoUpgradeToFullTip,
  };
}

export function useBrandSafetyAvailableCountries(adgroupFormData: AdgroupFormData) {
  getStore(StateEnum.brandSafetyAvailableCountryList, {
    objectiveType: adgroupFormData.objectiveType,
    classify: adgroupFormData.classify,
    inventoryFlow: adgroupFormData.inventoryFlow,
    brandSafety: BrandSafetyType.STANDARD_INVENTORY,
    switchToNewBrandSafetyFilter: true,
  });
}

/** 生成库存筛选选项 */
export function generateInventoryFilterOptions({
  isSupportExpandedTier = false,
  bscInfo = {},
  isExpandedFullDisabled = false,
}: {
  isSupportExpandedTier: boolean;
  bscInfo: BrandSafetyCenterInfo;
  isExpandedFullDisabled: boolean;
}) {
  const replaceFullToExpanded = (fullText: VueI18n.TranslateResult, expandedText: VueI18n.TranslateResult) =>
    isSupportExpandedTier ? expandedText : fullText;
  return [
    {
      value: BrandSafetyType.UN_SAFE,
      label: i18n.t('module_adgroup_placement_new_bs_full_inventory_name'),
      desc: i18n.t('module_adgroup_placement_new_bs_full_inventory_desc'),
      reach: {
        summary: i18n.t('module_adgroup_placement_new_bs_full_inventory_expanded_reach'),
        percentage: 100,
      },
      exclusionExamples: [
        i18n.t('module_adgroup_placement_new_bs_full_inventory_violence'),
        i18n.t('module_adgroup_placement_new_bs_full_inventory_sexual'),
      ],
      isDefault: bscInfo.brandSafetyType === BrandSafetyType.UN_SAFE,
      hide: isSupportExpandedTier,
      isDisabled: isExpandedFullDisabled,
      tooltipText: i18n.t('adgroup_brand_safety_disabled_tier_tip'),
    },
    {
      value: BrandSafetyType.EXPANDED_INVENTORY,
      label: i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_name'),
      desc: i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_desc'),
      reach: {
        summary: i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_reach'),
        percentage: 95,
      },
      exclusionExamples: [
        i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_violence'),
        i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_sexual'),
        i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_include'),
        i18n.t('module_adgroup_placement_new_bs_expanded_full_inventory_culture'),
      ],
      isDefault: bscInfo.brandSafetyType === BrandSafetyType.EXPANDED_INVENTORY,
      hide: !isSupportExpandedTier,
      isDisabled: isExpandedFullDisabled,
      tooltipText: i18n.t('adgroup_brand_safety_disabled_tier_tip'),
    },
    {
      value: BrandSafetyType.STANDARD_INVENTORY,
      label: i18n.t('module_adgroup_placement_new_bs_standard_inventory_name'),
      desc: i18n.t('module_adgroup_placement_new_bs_standard_inventory_desc'),
      reach: {
        summary: i18n.t('module_adgroup_placement_new_bs_standard_inventory_optimal_reach'),
        percentage: 90,
      },
      exclusionExamples: [
        i18n.t('module_adgroup_placement_new_bs_standard_inventory_violence'),
        i18n.t('module_adgroup_placement_new_bs_standard_inventory_sexual'),
        replaceFullToExpanded(
          i18n.t('module_adgroup_placement_new_bs_standard_inventory_include'),
          i18n.t('module_adgroup_placement_new_bs_standard_inventory_include_new'),
        ),
      ],
      isDefault: bscInfo.brandSafetyType === BrandSafetyType.STANDARD_INVENTORY,
      hide: false,
    },
    {
      value: BrandSafetyType.SELCTED_INVENTORY,
      label: i18n.t('module_adgroup_placement_new_bs_limited_inventory_name'),
      desc: i18n.t('module_adgroup_placement_new_bs_limited_inventory_desc'),
      reach: {
        summary: i18n.t('module_adgroup_placement_new_bs_limited_inventory_limited_reach'),
        percentage: 75,
      },
      exclusionExamples: [
        i18n.t('module_adgroup_placement_new_bs_limited_inventory_sexual'),
        i18n.t('module_adgroup_placement_new_bs_limited_inventory_violence'),
        replaceFullToExpanded(
          i18n.t('module_adgroup_placement_new_bs_limited_inventory_include'),
          i18n.t('module_adgroup_placement_new_bs_limited_inventory_include_new'),
        ),
      ],
      isDefault: bscInfo.brandSafetyType === BrandSafetyType.SELCTED_INVENTORY,
      hide: false,
    },
  ];
}
